import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import UploadFile from 'components/Form/UploadFile';
import ModalSolProtocolos from 'components/Modal/ModalSolProtocolos';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import { Read as ReadControls } from 'services/financing-desk-controls';
import { ReadCuradoriaSolControle as ReadControler } from 'services/financing-proposals';
import { Load, StatusContent, Title } from 'ui/styled';
import { exposeStrapiError, parseStrapiImage, selectColors } from 'utils';
import { formatElapsedTime } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

const normalizeField = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  return str === '' || str === '-' ? '' : str;
};

const TableInfo = ({ children }) => (
  <span style={{ color: 'blue' }}>{children}</span>
);
const TableInfoPend = ({ children }) => (
  <span style={{ color: 'red', cursor: 'pointer' }}>{children}</span>
);
const Touch = ({ onClick, children }) => (
  <span
    onClick={onClick}
    style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
  >
    {children}
  </span>
);

// -----------------------------------------------------------------------------
// Componente principal
// -----------------------------------------------------------------------------
export default function SolProtocolos() {
  const { user, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  // ============================================================
  // Função de upload / salvar fatura / parecer / protocolo
  // ============================================================
  const saveFile = useCallback(async ({ fatura, proposta, status, data }) => {
    if (!proposta || !status || !data) {
      toast.error('Dados incompletos.');
      return;
    }
    setUploadStatus((prev) => ({ ...prev, [proposta]: true }));

    try {
      let payload = {
        propostaId: proposta,
        data: {},
        attStatus: 'Analisado',
      };
      const statusLower = (status || '').toLowerCase().trim();
      // STATUS: Conexão e 1ª Parcela → só data no campo "conexao"
      if (statusLower === 'conexão e 1ª parcela') {
        payload.data = { conexao: data };
        // Outros status com mídia
      } else if (statusLower === 'instalação e protocolo de conexão') {
        payload.data = {
          protocolo_acesso_midia: fatura?.id,
          protocolo_acesso: data,
        };
      } else if (statusLower === 'primeira fatura') {
        payload.data = {
          primeira_fatura_midia: fatura?.id,
          primeira_fatura: data,
        };
      } else if (statusLower === 'entrega e parecer de acesso') {
        payload.data = {
          parecer_acesso_midia: fatura?.id,
          parecer_acesso: data,
        };
      } else {
        throw new Error('Status não reconhecido.');
      }
      const result = await POST(
        '/financing-desk-control/curadoriareagendar',
        payload,
        true
      );
      if (result && !exposeStrapiError(result)) {
        toast.success(`Confirmado com sucesso!`);
        await init();
      } else {
        toast.error('Falha ao salvar.');
      }
      return result;
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao processar.');
    } finally {
      setUploadStatus((prev) => ({ ...prev, [proposta]: false }));
    }
  }, []);

  // ============================================================
  // Init
  // ============================================================
  const init = async () => {
    setLoading(true);
    try {
      const deskResult = await ReadControls(`?populate[0]=proposta_financeira`);
      const controls = deskResult?.results ?? [];
      const proposalsResult = await ReadControler();
      if (!proposalsResult || exposeStrapiError(proposalsResult)) {
        toast.error('Erro ao carregar propostas');
        return;
      }
      const merged = proposalsResult.results?.map((p) => {
        const controlMatch = controls.find(
          (c) => c?.proposta_financeira?.id === p?.id
        );
        return { ...p, status: controlMatch?.status ?? p?.status };
      });
      setRegisters(merged);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => init(), []);

  // ============================================================
  // Colunas
  // ============================================================
  const columns = useMemo(() => {
    const baseCols = [
      { title: 'Id', sortable: true, ref: 'id' },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'SLA',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
    ];
    if (
      user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
    ) {
      baseCols.push({
        title: 'Distribuidor',
        sortable: true,
        ref: 'distributor',
      });
    }
    const moreCols = [
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Cidade', ref: 'cidade' },
      { title: 'UF', ref: 'estado' },
      { title: 'Banco', sortable: true, ref: 'banco' },
      { title: 'Status', ref: 'status' },
      {
        title: 'Tag',
        ref: 'agente/ocorrido',
        renderCell: ({ row }) => {
          const agente = normalizeField(row?.agente);
          const ocorrido = normalizeField(row?.ocorrido);
          const agenteStr = agente?.trim() || null;
          const ocorridoStr = ocorrido?.trim() || null;
          const hasAgente = !!agenteStr;
          const hasOcorrido = !!ocorridoStr;
          let tagKey = 'gray';
          if (hasAgente) tagKey = 'warning';
          else if (hasOcorrido) tagKey = 'secondary';
          const textParts = [];
          if (hasAgente) textParts.push(agenteStr);
          if (hasOcorrido) textParts.push(ocorridoStr);
          const text = textParts.length > 0 ? textParts.join(' / ') : '-';
          return (
            <StatusContent color={selectColors(tagKey)}>{text}</StatusContent>
          );
        },
      },
      {
        title: 'Telefone',
        renderCell: ({ row }) => {
          if (!row?.telefone || row?.telefone === '-') return '-';
          const numeroLimpo = row.telefone.replace(/\D/g, '');
          const linkWhats = `https://wa.me/55${numeroLimpo}`;
          return (
            <a
              href={linkWhats}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'blue' }}
            >
              {row.telefone}
            </a>
          );
        },
      },
      // COLUNA REAGENDAR - COM LOGICA DE HISTÓRICO
      {
        title: 'Reagendar',
        renderCell: ({ row }) => {
          const original = row.original;
          const qtdContato =
            original?.monitoring?.[0]?.attributes?.qtd_contato ??
            original?.monitoring?.attributes?.qtd_contato ??
            original?.monitoring?.qtd_contato ??
            0;
          const podeReagendar = qtdContato <= 1;
          if (!podeReagendar) {
            return (
              <span style={{ color: '#000000ff', fontSize: '1.0em' }}>
                Foi feito ({qtdContato} contatos)
              </span>
            );
          }
          return (
            <Button
              color="blue"
              nospace
              small
              mbutton
              onClick={() => setModalData(original)}
            >
              Reagendar
            </Button>
          );
        },
      },
      {
        title: 'Anexar',
        renderCell: ({ row }) => {
          const original = row.original;
          const statusLower = (row.status || '').toLowerCase().trim();
          const isUploading = uploadStatus[row.id];
          const cellStyle = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.0em',
          };
          // STATUS: Conexão e 1ª Parcela → só data
          if (statusLower === 'conexão e 1ª parcela') {
            const jaConfirmado = !!original?.conexao;
            if (isUploading) {
              return (
                <div style={cellStyle}>
                  <Load size={18} />
                  <div style={{ marginTop: '4px', color: '#666' }}>
                    Enviando...
                  </div>
                </div>
              );
            }
            if (jaConfirmado) {
              return (
                <div style={cellStyle}>
                  <TableInfo>Data Confirmada</TableInfo>
                  <div
                    style={{
                      fontSize: '0.7em',
                      color: '#28a745',
                      marginTop: '2px',
                    }}
                  >
                    {moment(original.conexao).format('DD/MM/YYYY')}
                  </div>
                </div>
              );
            }
            return (
              <div style={cellStyle}>
                <Button
                  color="blue"
                  small
                  nospace
                  mbutton
                  onClick={() =>
                    saveFile({
                      fatura: null,
                      proposta: row.id,
                      status: row.status,
                      data: moment().format('YYYY-MM-DD'),
                    })
                  }
                >
                  Confirmar Data
                </Button>
              </div>
            );
          }
          // Status com upload (antigos)
          const configMap = {
            'instalação e protocolo de conexão': {
              label: 'Protocolo',
              field: 'protocolo_acesso_midia',
            },
            'entrega e parecer de acesso': {
              label: 'Parecer',
              field: 'parecer_acesso_midia',
            },
            'primeira fatura': {
              label: 'Fatura',
              field: 'primeira_fatura_midia',
            },
          };
          const config = configMap[statusLower];
          if (!config) return <span>—</span>;
          const jaAnexado = !!original?.[config.field];
          const urlAnexo = parseStrapiImage(original?.[config.field]);
          if (isUploading) {
            return (
              <div style={cellStyle}>
                <Load size={18} />
                <div style={{ marginTop: '4px', color: '#666' }}>
                  Enviando...
                </div>
              </div>
            );
          }
          if (jaAnexado) {
            return (
              <div style={cellStyle}>
                <Touch onClick={() => window.open(urlAnexo, '_blank')}>
                  <TableInfo>Anexado</TableInfo>
                </Touch>
                <div
                  style={{
                    fontSize: '0.7em',
                    color: '#28a745',
                    marginTop: '2px',
                  }}
                >
                  {config.label}
                </div>
              </div>
            );
          }
          return (
            <div style={cellStyle}>
              <UploadFile
                key={`${row.id}-${config.label}`}
                onChange={(file) =>
                  saveFile({
                    fatura: file,
                    proposta: row.id,
                    status: row.status,
                    data: moment().format('YYYY-MM-DD'),
                  })
                }
                proposalType="A_Formalizar"
              >
                <TableInfoPend>{config.label}</TableInfoPend>
              </UploadFile>
            </div>
          );
        },
      },
    ];
    return [...baseCols, ...moreCols];
  }, [user, uploadStatus, saveFile]);
  // ============================================================
  // Linhas
  // ============================================================
  const rows = useMemo(() => {
    return registers?.map((item) => {
      let monitoring = item?.monitoring;
      if (Array.isArray(monitoring)) monitoring = monitoring[0];
      if (monitoring?.data?.attributes) monitoring = monitoring.data.attributes;
      return {
        id: item?.id,
        date: item?.createdAt,
        formatedDate: formatadores.TIMESTAMP_TO_BR(item?.createdAt),
        distributor: normalizeName(item?.distribuidor?.trade_name),
        integrator: normalizeName(item?.integrador?.nome_fantasia),
        customer: normalizeName(item?.informacoes_cliente?.nome_completo),
        cidade: normalizeName(item?.endereco_instalacao?.cidade),
        estado: item?.endereco_instalacao?.estado,
        telefone: monitoring?.telefone ?? '-',
        agente: monitoring?.agente,
        ocorrido: monitoring?.ocorrido ?? '-',
        banco:
          item?.propostas_banco?.find(
            (pb) => pb?.status?.toLowerCase() === 'aprovado'
          )?.banco?.nome_fantasia ?? '-',
        status: normalizeName(item?.status ?? '-'),
        original: item,
        // cnpj_integrador: item?.integrador?.cnpj_integrador,
        // cnpj_integrador_clean: item?.integrador?.cnpj_integrador,.replace(
        //   /\D/g,
        //   ''
        // ),
        // cpf_cnpj: item?.informacoes_cliente?.cpf || item?.informacoes_cliente?.cnpj,
        // cpf_cnpj_clean: a?.proposta_financeira?.cpf
        //   ? item?.informacoes_cliente?.cpf.replace(/\D/g, '')
        //   : item?.informacoes_cliente?.cnpj.replace(/\D/g, ''),
      };
    });
  }, [registers]);
  //console.log(registers);
  return (
    <ContainerAuthenticated>
      <Title>Curadoria</Title>
      <TableWrapper title="SOL | Protocolos" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
      {modalData && (
        <ModalSolProtocolos
          proposalData={modalData}
          reload={init}
          onClose={() => setModalData(null)}
        />
      )}
    </ContainerAuthenticated>
  );
}
